"use client";

import React from "react";
import { AlertTriangle } from "lucide-react";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { cn } from "@/app/_module/lib/utils";
import { Panel, focusRing } from "./overview/primitives";
import { Choice, ChoiceGroup, StepIntro } from "./overview/wizard-ui";

export type RegistrationType = 'sole_proprietor' | 'low_volume_standard';

interface Step0RegistrationTypeSelectProps {
  selectedType: RegistrationType | null;
  onSelectType: (type: RegistrationType) => void;
  /** Owner/admin of the current agency — only they may register on its behalf. */
  canRegisterForAgency?: boolean;
  agencyName?: string;
  registerForAgency?: boolean;
  onToggleRegisterForAgency?: (value: boolean) => void;
  coversDownline?: boolean;
  onChangeCoversDownline?: (value: boolean) => void;
  riskAccepted?: boolean;
  onChangeRiskAccepted?: (value: boolean) => void;
}

// Same rows in the same order on both cards so they compare line by line.
const TYPES: { value: RegistrationType; title: string; forWhom: string; rows: [string, string][] }[] = [
  {
    value: 'sole_proprietor',
    title: 'Sole proprietor',
    forWhom: 'For an individual agent without an EIN.',
    rows: [
      ["You'll need", 'Your legal name and the last 4 digits of your SSN'],
      ['Numbers that can text', 'One'],
      ['Daily limit', 'About 1,000 texts, varies by carrier'],
    ],
  },
  {
    value: 'low_volume_standard',
    title: 'Low Volume Standard',
    forWhom: 'For a business or agency with an EIN.',
    rows: [
      ["You'll need", 'Your EIN and the IRS CP 575 letter'],
      ['Numbers that can text', 'All of them, including numbers you buy later'],
      ['Daily limit', 'Up to 6,000 message segments'],
    ],
  },
];

const checkboxClass = cn("mt-0.5 size-[18px] rounded-[5px]", focusRing);

export default function Step0RegistrationTypeSelect({
  selectedType,
  onSelectType,
  canRegisterForAgency = false,
  agencyName,
  registerForAgency = false,
  onToggleRegisterForAgency,
  coversDownline = true,
  onChangeCoversDownline,
  riskAccepted = false,
  onChangeRiskAccepted,
}: Step0RegistrationTypeSelectProps) {
  const agency = agencyName || "your agency";

  return (
    <div className="space-y-8">
      <StepIntro title="Choose your registration type">
        <p>It sets how the carriers check your business and how many of your numbers can text.</p>
      </StepIntro>

      <ChoiceGroup
        aria-label="Registration type"
        value={selectedType ?? ""}
        onValueChange={(v) => onSelectType(v as RegistrationType)}
        className="sm:grid-cols-2"
      >
        {TYPES.map((t) => (
          <Choice key={t.value} value={t.value} title={t.title} description={t.forWhom}>
            <span className="mt-4 block space-y-3 border-t border-neutral-100 pt-4">
              {t.rows.map(([label, value]) => (
                <span key={label} className="block">
                  <span className="block text-xs text-neutral-500">{label}</span>
                  <span className="mt-0.5 block text-sm leading-relaxed text-neutral-900">{value}</span>
                </span>
              ))}
            </span>
          </Choice>
        ))}
      </ChoiceGroup>

      {/* Agency-wide coverage. Only offered on Low Volume Standard: a sole prop
          campaign registers exactly ONE number, so it cannot cover a downline. */}
      {canRegisterForAgency && selectedType === 'low_volume_standard' && (
        <Panel className={cn("transition-colors", registerForAgency && "border-primary")}>
          <label className="flex cursor-pointer items-start gap-3 px-4 py-4 sm:px-5">
            <Checkbox
              checked={registerForAgency}
              onCheckedChange={(v) => onToggleRegisterForAgency?.(v === true)}
              className={checkboxClass}
            />
            <span className="block space-y-1.5">
              <span className="block text-sm font-medium text-neutral-900">Register once for {agency}</span>
              <span className="block text-pretty text-sm leading-relaxed text-neutral-600">
                Every agent below you, including sub-agencies, can text from their own numbers under this one
                registration. New numbers they buy are added automatically, and agents who already registered on
                their own keep what they have.
              </span>
              <span className="block text-pretty text-sm leading-relaxed text-neutral-600">
                You&apos;re registering on the agency&apos;s behalf with its legal business details, and the daily
                limit is shared by everyone it covers.
              </span>
            </span>
          </label>

          {registerForAgency && (
            <div className="space-y-5 border-t border-neutral-100 px-4 py-5 sm:px-5">
              {/* Scope. A sub-agency that registers its own campaign already takes
                  precedence over a parent's, so this is only about whether a parent
                  reaches down to sub-agencies that have NOT registered. */}
              <div className="space-y-2.5">
                <p id="a2p-scope-label" className="text-sm font-medium text-neutral-900">
                  Who does this cover?
                </p>
                <ChoiceGroup
                  aria-labelledby="a2p-scope-label"
                  value={coversDownline ? "downline" : "agency_only"}
                  onValueChange={(v) => onChangeCoversDownline?.(v === "downline")}
                >
                  <Choice
                    value="downline"
                    title="Everyone under me"
                    description="This agency and every sub-agency below it. A sub-agency that registers its own later takes over for its own people automatically."
                  />
                  <Choice
                    value="agency_only"
                    title="This agency only"
                    description={`Only agents in ${agencyName || "this agency"} itself. Sub-agencies register their own, and their agents can't text until they do.`}
                  />
                </ChoiceGroup>
              </div>

              {/* Shared-fate acknowledgement. Required; the backend rejects without it. */}
              <label className="flex cursor-pointer items-start gap-3 rounded-lg bg-amber-50 p-4 text-amber-800">
                <Checkbox
                  checked={riskAccepted}
                  onCheckedChange={(v) => onChangeRiskAccepted?.(v === true)}
                  className={cn(checkboxClass, "border-amber-700 data-[state=checked]:bg-amber-700")}
                />
                <span className="block space-y-1.5 text-sm leading-relaxed">
                  <span className="flex items-center gap-2 font-semibold">
                    <AlertTriangle aria-hidden className="size-4" />
                    I accept the shared risk
                  </span>
                  <span className="block text-pretty">
                    Carriers act against the registration, not the individual sender. If any agent it covers sends
                    spam, ignores opt-outs or gets reported, the carriers can filter or suspend it, and{" "}
                    <strong className="font-semibold">every agent it covers loses texting at the same moment</strong>,
                    not just the one who caused it.
                  </span>
                  <span className="block text-pretty">
                    The daily limit is shared by everyone covered, and I&apos;m responsible for what my agents send
                    under my agency&apos;s name.
                  </span>
                </span>
              </label>
            </div>
          )}
        </Panel>
      )}

      <p className="text-pretty text-sm leading-relaxed text-neutral-500">
        Not sure? Choose sole proprietor if you don&apos;t have an EIN. You can move to Low Volume Standard later
        through support.
      </p>
    </div>
  );
}
